import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useAuth } from '../../contexts/AuthContext';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface GradeLevelData {
  [grade: string]: number;
}

const GradeLevelChart: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<GradeLevelData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGradeLevelData();
  }, []);

  const fetchGradeLevelData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/charts/grade-levels', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch grade level data: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching grade level data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Generate colors for the donut chart
  const generateColors = (count: number) => {
    const baseColors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#C9CBCF', '#8BC34A', '#E91E63', '#673AB7',
      '#009688', '#FF5722', '#795548', '#607D8B', '#FFC107'
    ];
    
    return Array(count).fill(0).map((_, index) => baseColors[index % baseColors.length]);
  };

  // Sort grades in logical order
  const sortGrades = (grades: string[]) => {
    return grades.sort((a, b) => {
      if (a === 'Not Set') return 1;
      if (b === 'Not Set') return -1;
      
      // Extract numeric part if present
      const numA = parseInt(a.replace(/[^\d]/g, ''));
      const numB = parseInt(b.replace(/[^\d]/g, ''));
      
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      
      // Fallback to alphabetical
      return a.localeCompare(b);
    });
  };

  const sortedGrades = sortGrades(Object.keys(data));
  const totalStudents = Object.values(data).reduce((sum, count) => sum + count, 0);

  const chartData = {
    labels: sortedGrades,
    datasets: [
      {
        label: 'Students',
        data: sortedGrades.map(grade => data[grade]),
        backgroundColor: generateColors(sortedGrades.length),
        borderColor: generateColors(sortedGrades.length).map(color => color.replace('0.8', '1')),
        borderWidth: 2,
        hoverOffset: 15,
        cutout: '60%', // This makes it a donut chart
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 12,
          },
          color: '#2c3e50',
        },
      },
      title: {
        display: true,
        text: 'Grade Level Distribution',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        color: '#2c3e50',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            const percentage = ((context.raw / totalStudents) * 100).toFixed(1);
            const studentText = context.raw === 1 ? 'student' : 'students';
            return `${context.label}: ${context.raw} ${studentText} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 2000,
      easing: 'easeInOutCubic' as const,
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const,
    },
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading grade level data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error">
        <p>Error loading chart: {error}</p>
        <button onClick={fetchGradeLevelData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (Object.keys(data).length === 0) {
    return (
      <div className="chart-empty">
        <p>No grade level data available</p>
        <p className="chart-empty-detail">Add grade levels to students to see distribution</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        <Doughnut data={chartData} options={options} />
        {totalStudents > 0 && (
          <div className="chart-center-text">
            <div className="total-students">
              <span className="number">{totalStudents}</span>
              <span className="label">Total Students</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GradeLevelChart;
